import { Context, ExternalContextDetail, ContextInternal } from "./context";
import { AppContext } from "./application-context";
import {
  WindowHeartbeatService,
  open as WindowStatusOpen,
  closed as WindowStatusClosed,
  WindowStatus
} from "../../../ShellCommon/eventModule/window-heartbeat-service";
import { WindowCommunicationBridgeService } from "../../../ShellCommon/eventModule/window-communication-bridge";
import { ApplicationContextService } from "./application-context-service";
import { StorageWrapperService, memoryLeakEntries, entries } from "../local-storage-wrapper-service";
import { recoveryBroadcastEvent } from "../../recovery/recovery-manager";
import { AuditService } from "../audit/audit-service";

export interface ContextService {
  /** The current context */
  readonly current: Context;

  /** The shared application context */
  readonly appContext: AppContext;

  /** Retrieve a context by id */
  context(id: string): ng.IPromise<Context>;

  /** Get all active contexts */
  activeContexts(): ng.IPromise<Context[]>;

  /** Set extend context data to keep with this context */
  setExtendedContextData(key: string, value: string): void;

  /** Get extend context data */
  getExtendedContextData(key: string): string;

  /**Set external details to be added in ServiceDesk telemetry events. */
  setExternalDetail(externalDetail: ExternalContextDetail): void;
}

/**
 * comms agent presence information
 * TODO: See 11365154, This is deprecated and will be removed
 */
// tslint:disable:variable-name
export const PresenceDataKey = "comm.presence";

/** The windows communication bridge event name when a context is updated */
export const ContextUpdatedEventName = "ContextUpdatedEvent";

/** The windows communication bridge event arguments when a context is updated */
export interface UpdatedEvent {
  /** The context id that was updated */
  contextId: string;

  /** Optional, the specific key that was updated */
  key?: string;

  /** Optional, the specific value that was updated */
  value?: string;
}

const initialContextLoadingName = "application_loading";
const contextPrefix = "ctxt_";
export const savedSessionContextKey = "ctxt_saved";
export const contextIdSessionStorageKey = "current_context_id";
export const previousContextIdSessionStorageKey = "previous_context_id";

export interface SessionStorageContext {
  storageKey: string;
  storageValue: string;
}

export class ContextServiceImpl implements ContextService {
  public current: ContextInternal;
  private localStorage: Storage;

  // @ngInject
  constructor(
    private $rootScope: ng.IRootScopeService,
    private $q: ng.IQService,
    private $interval: ng.IIntervalService,
    private $location: ng.ILocationService,
    private $window: ng.IWindowService,
    private windowsHeartbeatService: WindowHeartbeatService,
    private windowCommunicationBridgeService: WindowCommunicationBridgeService,
    private applicationContextService: ApplicationContextService,
    private $injector: ng.auto.IInjectorService
  ) {
    this.localStorage = StorageWrapperService.getInstance();
    let savedContextsFromRefresh = $window.sessionStorage.getItem(savedSessionContextKey);
    if (savedContextsFromRefresh) {
      $window.sessionStorage.removeItem(savedSessionContextKey);
      let storageContexts: SessionStorageContext[] = JSON.parse(savedContextsFromRefresh);
      storageContexts.forEach((storageContext) =>
        this.localStorage.setItem(storageContext.storageKey, storageContext.storageValue)
      );
    }
    this.createStateChangeWatcher(undefined, { name: initialContextLoadingName, params: undefined }, {});
    $rootScope.$on("$stateChangeSuccess", this.createStateChangeWatcher.bind(this));
  }

  public replaceCurrentContext(replacementContext: ContextInternal) {
    this.clearSerializedContext(this.current);
    this.addOrUpdateSerializedContext(replacementContext);
    this.current = replacementContext;
    let oldContextId = this.$window.sessionStorage.getItem(contextIdSessionStorageKey);
    if (oldContextId && oldContextId != replacementContext.id) {
      this.$window.sessionStorage.setItem(previousContextIdSessionStorageKey, oldContextId);
    }
    this.$window.sessionStorage.setItem(contextIdSessionStorageKey, replacementContext.id);
  }

  public recoverContext(contextId: string) {
    this.context(contextId).then((matchingContext: ContextInternal) => {
      let restoredContext = angular.copy(matchingContext);
      restoredContext.creationTime = new Date();
      restoredContext.status = WindowStatusOpen;
      restoredContext.id = this.windowsHeartbeatService.getCurrentWindowId();
      restoredContext.extendedData = new Map<string, string>(restoredContext.extendedData);
      restoredContext.wasRecovered = true;
      this.replaceCurrentContext(restoredContext);
      this.$rootScope.$broadcast(recoveryBroadcastEvent);
    });
  }

  public get appContext(): AppContext {
    return this.applicationContextService.appContext;
  }

  setExternalDetail(contextDetail: ExternalContextDetail) {
    this.current.externalContextDetail = angular.extend(this.current.externalContextDetail || {}, contextDetail);
  }

  public setExtendedContextData(key: string, value: string) {
    this.current.extendedData = this.current.extendedData || new Map<string, string>();
    this.current.extendedData.set(key, value);
    let eventData: UpdatedEvent = {
      contextId: this.current.id,
      key: key,
      value: value
    };
    this.windowCommunicationBridgeService.publish(ContextUpdatedEventName, eventData);
    this.addOrUpdateSerializedContext(this.current);
  }

  public getExtendedContextData(key: string) {
    if (!this.current.extendedData) {
      return undefined;
    }
    return this.current.extendedData.get(key);
  }

  public context(id: string): ng.IPromise<Context> {
    return this.contexts().then((contexts) => {
      return contexts.find((c) => c.id == id);
    });
  }

  public activeContexts(): ng.IPromise<Context[]> {
    return this.contexts().then((contexts) => contexts.filter((c) => c.status == WindowStatusOpen));
  }

  public contexts(): ng.IPromise<Context[]> {
    return this.$q.resolve(this.contextsSync());
  }

  private contextsSync(): ContextInternal[] {
    let windows: WindowStatus[] = this.windowsHeartbeatService.getWindows();

    let contexts: ContextInternal[] = [];
    let localStorageEntries = entries(this.localStorage);

    localStorageEntries.forEach((value: any, key: any) => {
      if (!key.startsWith(contextPrefix)) {
        return;
      }
      let context = JSON.parse(value) as ContextInternal;
      if (context.stateName !== initialContextLoadingName) {
        contexts.push(context);
      }
    });

    for (let i = 0; i < contexts.length; i++) {
      let windowDetails = windows.find((window) => window.windowId == contexts[i].id);
      contexts[i].status = windowDetails ? windowDetails.status : WindowStatusClosed;
    }

    return contexts;
  }

  private addOrUpdateSerializedContext(context: Context): void {
    this.localStorage.setItem(`${contextPrefix}${context.id}`, JSON.stringify(context));
  }

  public clearSerializedContext(context: Context): void {
    if (!context) {
      return;
    }
    this.localStorage.removeItem(`${contextPrefix}${context.id}`);
  }

  private clearAllContext(): void {
    let savedSessionContexts: SessionStorageContext[] = [];
    memoryLeakEntries().forEach((value: string, key: string) => {
      if (key.startsWith(contextPrefix)) {
        savedSessionContexts.push({ storageKey: key, storageValue: value });
        this.localStorage.removeItem(key);
      }
    });
    // save in session storage in case this is just a refresh
    this.$window.sessionStorage.setItem(savedSessionContextKey, JSON.stringify(savedSessionContexts));
    this.applicationContextService.shutdownApp();
  }

  private isTheOnlyActiveWindow(): boolean {
    let activeWindows = this.windowsHeartbeatService
      .getWindows()
      .filter((window) => window.status === WindowStatusOpen);
    return activeWindows.length == 1 && activeWindows[0].windowId == this.windowsHeartbeatService.getCurrentWindowId();
  }

  private createStateChangeWatcher(event: any, toState: ng.ui.IState, toParams: ng.ui.IStateParamsService) {
    // While we are in the middle of restoring a page we should not attempt to update any context information triggered via state changes
    if (toState.name == "sdSystem.restore") {
      return;
    }
    let url = this.$location.absUrl();
    let newContext: ContextInternal = {
      id: this.windowsHeartbeatService.getCurrentWindowId(),
      creationTime: new Date(),
      status: WindowStatusOpen,
      url: url,
      stateName: toState.name,
      stateParameters: toState.params,
      wasRecovered: this.current ? this.current.wasRecovered : false
    };
    this.current = angular.extend(angular.copy(this.current || {}), newContext);
    this.applicationContextService.incrementCV();
    this.addOrUpdateSerializedContext(this.current);
  }

  public shutdown() {
    if (this.isTheOnlyActiveWindow() && !this.applicationContextService.isSigningIn()) {
      let msalAuthenticationService = this.$injector.get<any>("msalAuthenticationService");
      if (msalAuthenticationService.userInfo && msalAuthenticationService.userInfo.isAuthenticated) {
        let auditService = this.$injector.get<AuditService>("auditService");
        auditService.recordAction("SessionClosed");
      }
      this.clearAllContext();
    }
    this.windowsHeartbeatService.shutdownThisWindowGracefully();
  }
}

// @ngInject
export const onRunForContextService = (contextService: ContextServiceImpl, $window: ng.IWindowService) => {
  // If we are in an iframe, do not track for a shutdown to cleanup context/heartbeats
  if ($window == $window.top) {
    $window.addEventListener("unload", contextService.shutdown.bind(contextService));
  }
};
